import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Card, Button, Badge } from "react-bootstrap";
import { Modal } from "react-responsive-modal";
import { useToasts } from "react-toast-notifications";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faList, faTrash, faUserPlus, faTimes, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Loader from "components/Loader";
import ShareCourseBuilder from "components/CourseBuilder/ShareCourseBuilder/ShareCourseBuilder";
import CourseService from "services/CourseService";
import { useUserContext } from "store/contexts/userContext";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import DefaultTeamAvatar from "assets/img/HandsColorAvatar.jpeg";

const TeamCourseManage = () => {
  const { teamId, id } = useParams();
  const history = useHistory();
  const { addToast } = useToasts();
  const { user } = useUserContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [course, setCourse] = useState(null);
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [teamToUnassign, setTeamToUnassign] = useState(null);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [isAssignedCourse, setIsAssignedCourse] = useState(false);

  const fetchCourseData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await CourseService.getCourse(teamId, id);
      
      if (response && response.data) {
        const courseData = response.data.data;
        setCourse(courseData);
        
        if (courseData.assigned_teams) {
          setAssignedTeams(courseData.assigned_teams);
        }
        
        // Check if this is a course assigned to the team but not created by the current user
        if (courseData.created_by) {
          const isCreatedByOther = Number(courseData.created_by) !== Number(user?.id);
          const isAssigned = courseData.assigned_teams && 
            courseData.assigned_teams.some(team => 
              Number(team.id) === Number(teamId));
          
          setIsAssignedCourse(isCreatedByOther && isAssigned);
        }
      } else {
        throw new Error('Course not found');
      }
    } catch (err) {
      console.error("Error fetching course:", err);
      setError(err.message || "Failed to load course data");
    } finally {
      setLoading(false);
    }
  }, [id, teamId, user?.id]);

  useEffect(() => {
    fetchCourseData();
  }, [id, teamId, user?.id, fetchCourseData]);

  const refreshAssignedTeams = () => {
    fetchCourseData();
  };

  const handleUnassignTeam = (team) => {
    setTeamToUnassign(team);
    setShowUnassignModal(true);
  };

  const confirmUnassignTeam = async () => {
    if (!teamToUnassign) return;
    
    try {
      setUnassignLoading(true);
      await CourseService.unassignCourseFromTeam({
        course_id: course.id,
        team_ids: [Number(teamToUnassign.id)]
      });
      
      addToast("Course unassigned successfully", { appearance: "success" });
      setShowUnassignModal(false);
      refreshAssignedTeams();
    } catch (error) {
      console.error("Error unassigning course:", error);
      addToast("Failed to unassign course", { appearance: "error" });
    } finally {
      setUnassignLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "published":
        return "success";
      case "draft":
        return "secondary";
      default:
        return "info";
    }
  };

  const handlePreview = () => {
    history.push(`/teams/${teamId}/coursebuilder/${course.id}/preview`);
  };
  const handleEdit = () => {
    history.push(`/teams/${teamId}/coursebuilder/${course.id}/edit`);
  };
  const handleDelete = () => {
    setShowConfirmModal(true);
  };
  const confirmDeleteCourse = async () => {
    try {
      setLoading(true);
      await CourseService.deleteCourse(teamId, course.id);
      addToast("Course deleted successfully", { appearance: "success" });
      setShowConfirmModal(false);
      history.push(`/teams/${teamId}/coursebuilder`);
    } catch (error) {
      console.error("Error deleting course:", error);
      addToast("Failed to delete course", { appearance: "error" });
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Button 
          variant="outline-secondary" 
          onClick={() => history.push(`/teams/${teamId}/coursebuilder`)}
        >
          <FontAwesomeIcon icon={faList} className="me-2" />
          Back to Courses
        </Button>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <div className="container-fluid py-4 manage-course">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button 
          variant="link" 
          className="p-0 text-decoration-none" 
          onClick={() => history.push(`/teams/${teamId}/coursebuilder`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Courses
        </Button>
        {!isAssignedCourse && (
          <div className="d-flex gap-2 course-manage-buttons">
            <Button variant="outline-secondary" size="sm" title="Preview Course" onClick={handlePreview}>
              <FontAwesomeIcon icon={faEye} /> Preview
            </Button>
            <Button variant="outline-primary" size="sm" title="Edit Course" onClick={handleEdit}>
              <FontAwesomeIcon icon={faEdit} /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" title="Delete Course" onClick={handleDelete}>
              <FontAwesomeIcon icon={faTrash} /> Delete
            </Button>
          </div>
        )}
      </div>
      <h3 className="mb-2"><strong>{course.title}</strong></h3>
      <div className="mb-2 text-muted">
        Last updated: {moment(course.updatedAt).format("MMM DD, YYYY")} <span className={`course-status-pill badge bg-${getStatusClass(course.status)} ms-3`}>{course.status.charAt(0).toUpperCase() + course.status.slice(1)}</span>
      </div>
      <div className="row">
        {/* Course Details */}
        <div className="col-12">
          <Card className="mb-4">
            <div className="row g-0">
              <div className="col-md-4 d-flex align-items-center justify-content-center p-3">
                <img
                  src={course.thumbnail}
                  alt="Course Cover"
                  className="img-fluid rounded"
                  style={{ height: "12rem", objectFit: "cover", width: "100%" }}
                />
              </div>
              <div className="col-md-8">
                <Card.Body>
                  <h5 className="mb-2"><strong>Course Details</strong></h5>
                  <p>{course.description}</p>
                  <div className="row mb-2">
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: 14 }}>Created</div>
                      <div><strong>{moment(course.createdAt).format("MMM DD, YYYY")}</strong></div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: 14 }}>Last Updated</div>
                      <div><strong>{moment(course.updatedAt).format("MMM DD, YYYY")}</strong></div>
                    </div>
                  </div>
                  <div className="row mt-4">
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: 14 }}>Modules</div>
                      <div><strong>{course.modules.length}</strong></div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: 14 }}>Lessons</div>
                      <div><strong>{course.modules.reduce((acc, m) => acc + m.lessons.length, 0)}</strong></div>
                    </div>
                  </div>
                </Card.Body>
              </div>
            </div>
          </Card>
        </div>

        {/* Course Content */}
        <div className="col-12">
          <Card className="mb-4">
            <Card.Header><h5 className="mb-0"><strong>Course Content</strong></h5></Card.Header>
            <Card.Body>
              {course.modules.map((module, idx) => (
                <div key={module.id} className="mb-3">
                  <div className="fw-bold mb-2"><h6 className="mb-3">Module {idx + 1}: {module.title} <span className="text-muted" style={{ fontWeight: 400, fontSize: 14 }}>({module.lessons.length} lessons)</span></h6></div>
                  <div className="list-group">
                    {module.lessons.map((lesson, lidx) => (
                      <div key={lesson.id} className="list-group-item d-flex align-items-center justify-content-between">
                        <h6 className="mb-0">
                          <span className="me-2 text-muted">{lidx + 1}.</span>
                          <span className="fw-bold">{lesson.title}</span>
                          <Badge bg="primary" className="ms-3" style={{ fontSize: 12 }}>{lesson.type}</Badge>
                        </h6>
                        <span className="text-muted" style={{ fontSize: 14 }}>{lesson.duration} min</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </div>

        {/* Course Assignments - Only shown for courses created by the user */}
        {!isAssignedCourse && (
          <div className="col-12">
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span><h5 className="mb-0"><strong>Course Assignments</strong></h5></span>
                <ShareCourseBuilder
                  course={course}
                  teamId={teamId}
                  onAssignmentComplete={refreshAssignedTeams}
                  renderButton={({ onClick, disabled, title }) => (
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={onClick}
                      disabled={disabled}
                      title={title}
                    >
                      <FontAwesomeIcon icon={faUserPlus} /> Assign
                    </Button>
                  )}
                />
              </Card.Header>
              <Card.Body>
                {assignedTeams.length === 0 ? (
                  <div className="text-muted">No teams assigned yet.</div>
                ) : (
                  <div className="row">
                    {assignedTeams.map(team => (
                      <div key={team.id} className="col-md-6 mb-3">
                        <div className="d-flex align-items-center">
                          <img src={team.avatar ?? DefaultTeamAvatar} alt={team.name} className="rounded-circle me-3" style={{ width: 40, height: 40, objectFit: "cover" }} />
                          <div className="flex-grow-1">
                            <div className="fw-bold">
                              {team.name}
                              {team.is_external && (
                                <span 
                                  className="ms-2 badge" 
                                  style={{ 
                                    backgroundColor: '#ffa100', 
                                    fontSize: '10px', 
                                    fontWeight: 'normal',
                                    padding: '2px 6px' 
                                  }}
                                >
                                  External
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            {team?.completion ? <span className={`course-status-pill badge bg-${team?.completion === 100 ? "success" : team?.completion < 50 ? "warning" : "info"} me-2`}>{team?.completion}% Complete</span> : null}
                            {team.id !== teamId && (
                              <Button
                                variant="outline-danger" 
                                size="sm" 
                                className="btn-icon"
                                onClick={() => handleUnassignTeam(team)}
                                title="Unassign course"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        showCloseIcon={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        classNames={{
          modalContainer: "modalContainer",
          modal: "createCourseModal",
        }}
      >
        <div className="modal fade popup-modal create-task-popup">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-center createTaskHeading">Delete Course</h5>
            <CloseIcon style={{ width: "20px", cursor: "pointer" }} onClick={() => setShowConfirmModal(false)} />
          </div>
          <div className="mb-4">
            <p>Are you sure you want to delete this course? This action cannot be undone.</p>
          </div>
          <div className="d-flex justify-content-end">
            <Button className="btn-secondary me-2" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button className="btn-danger" onClick={confirmDeleteCourse}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Unassign Confirmation Modal */}
      <Modal
        open={showUnassignModal}
        onClose={() => setShowUnassignModal(false)}
        showCloseIcon={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        classNames={{
          modalContainer: "modalContainer",
          modal: "createCourseModal",
        }}
      >
        <div className="modal fade popup-modal create-task-popup">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-center createTaskHeading">Unassign Course</h5>
            <CloseIcon style={{ width: "20px", cursor: "pointer" }} onClick={() => setShowUnassignModal(false)} />
          </div>
          <div className="mb-4">
            <p>Are you sure you want to unassign this course from {teamToUnassign?.name}?</p>
          </div>
          <div className="d-flex justify-content-end">
            <Button className="btn-secondary me-2" onClick={() => setShowUnassignModal(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-danger" 
              onClick={confirmUnassignTeam}
              disabled={unassignLoading}
            >
              {unassignLoading ? <Loader width={20} height={20} /> : "Unassign"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TeamCourseManage;
